import './App.css';
import './css/forms.css';
import './css/default-breakpoints.css';
import './css/alerts.css';
import './css/buttons.css';
import { BrowserRouter, Routes, Route } from "react-router-dom";
import ListingDetailsIndex from './listing-details';
import ListingSearch from './listing-search';
import InitializeMapCenter from './listing-search/initialize-map-center';
import { useJsApiLoader } from '@react-google-maps/api';
import { GOOGLE_MAPS_APIKEY, GOOGLE_MAPS_DRAWING_LIBRARIES, GOOGLE_MAPS_SCRIPT_ID } from './util/google.constants';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ICoord, defaultValue as defaultCoord } from './models/coord.model';
import { geocodeByPlaceId } from 'react-google-places-autocomplete';
import { HelmetProvider } from 'react-helmet-async';
import { IListings, defaultValue as defaultIListings } from './models/listings.model';
import { IListing } from './models/listing.model';
import { exampleBranding } from './example-data/branding';
import { ENV } from './util/defaults';
import { IBranding } from './models/branding.model';
import { ISearchParamsCache } from './models/search-params-cache.model';

function mapAttrsToSearchParamsCache() {
  const tmp: ISearchParamsCache = {};
  const myDiv = document.getElementById('realweb-listings-addons-root');

  if (myDiv !== null) {
    tmp['geo_state'] = myDiv.getAttribute('data-geo-state') || '';
    tmp['geo_street_no'] = myDiv.getAttribute('data-geo-street-no') || '';
    tmp['geo_street'] = myDiv.getAttribute('data-geo-street') || '';
    tmp['geo_street_suffix'] = myDiv.getAttribute('data-geo-street-suffix') || '';
    tmp['geo_city'] = myDiv.getAttribute('data-geo-city') || '';
    tmp['geo_community'] = myDiv.getAttribute('data-geo-community') || '';
  }
  return tmp;
}

function getSearchType() {
  const myDiv = document.getElementById('realweb-listings-addons-root');
  if (myDiv !== null) {
    if (myDiv.getAttribute('data-module') === 'listing-search-flat') {
      return 'flat'
    } else if (myDiv.getAttribute('data-module') === 'address-listings-results') {
      return 'flat'
    }
  }
  return 'map';
}

function getHeaderDisabled() {
  const myDiv = document.getElementById('realweb-listings-addons-root');
  if (myDiv !== null) {
    if (myDiv.getAttribute('data-module') === 'address-listings-results') {
      return true;
    }
  }
  return false;
}

function App() {

  const searchType = useMemo(() => {
    return getSearchType()
  }, []);

  const isHeaderDisabled = useMemo(() => {
    return getHeaderDisabled()
  }, []);

  const forcedSearchParams = useMemo(() => { return mapAttrsToSearchParamsCache() }, []);
  const mapCenterCoord = useRef<ICoord>(defaultCoord);
  const mapFitBounds = useRef<google.maps.LatLngBounds | null>(null);
  const [isMapCenterInitialized, setIsMapCenterInitialized] = useState(false);
  const userLocation = useRef<ICoord | null>(null);
  const prevSearchUrl = useRef<string>('');
  const [locationAutocompleteValue, setLocationAutocompleteValue] = useState<google.maps.places.AutocompletePrediction | null>(null);
  const [savedListings, setSavedListings] = useState<IListings>(defaultIListings);
  const [, setForceUpdate] = useState(Date.now());
  const [branding, setBranding] = useState<IBranding>({});

  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: GOOGLE_MAPS_APIKEY,
    id: GOOGLE_MAPS_SCRIPT_ID,
    language: 'en',
    region: 'EN',
    version: 'weekly',
    libraries: GOOGLE_MAPS_DRAWING_LIBRARIES
  });

  const isSavedListing = useCallback(function (id: string) {
    return savedListings.properties.findIndex(l => l.id === id) !== -1
  }, [savedListings.properties]);

  const indexOfSavedListing = useCallback(function (value: IListing) {
    let i = 0;
    while (i < savedListings.properties.length) {
      if (savedListings.properties[i].id === value.id) {
        return i;
      }
      i++;
    }
    return -1;
  }, [savedListings.properties]);

  const addSavedListing = useCallback(function (value: IListing | null) {
    value !== null && !isSavedListing(value.id) && setSavedListings({ ...savedListings, total: savedListings.total + 1, page_size: 50, resp_type: 'list', properties: savedListings.properties.concat([value]) });
  }, [isSavedListing, savedListings]);

  const removeSavedListing = useCallback(function (value: IListing | null) {
    value !== null && isSavedListing(value.id) &&
      setSavedListings(
        { ...savedListings, total: savedListings.total - 1, page_size: 50, resp_type: 'list', properties: savedListings.properties.slice(0, indexOfSavedListing(value)).concat(savedListings.properties.slice(indexOfSavedListing(value) + 1)) }
      );
  }, [indexOfSavedListing, isSavedListing, savedListings]);

  const setPrevSearchUrl = useCallback(function (value: string) {
    prevSearchUrl.current = value;
  }, []);

  const setMapCenterCoord = useCallback(function (value: ICoord, forceUpdate: boolean = false, updateIfInitialized: boolean = true) {
    if (!value.lat || (mapCenterCoord.current.lat === value.lat && mapCenterCoord.current.lng === value.lng)) {
      // invalid or same coordinates - intentionally empty
    } else {
      if (!updateIfInitialized && isMapCenterInitialized) {
        // intentionally empty
      } else {
        mapCenterCoord.current = value;
        forceUpdate && setForceUpdate(Date.now());
        !isMapCenterInitialized && setIsMapCenterInitialized(true);
      }
    }
  }, [isMapCenterInitialized]);

  const setMapFitBounds = useCallback(function (bounds: google.maps.LatLngBounds) {
    mapFitBounds.current = bounds;
    const boundCenter = bounds.getCenter();
    setMapCenterCoord({ lat: boundCenter.lat(), lng: boundCenter.lng() }, true);
  }, [setMapCenterCoord]);

  const setToUserLocation = useCallback(function (doCenterMap?: boolean, backupCoord?: ICoord, doErrorAlert: boolean = true) {
    if (navigator.geolocation) {
      //Dummy one, which will result in a working next statement. - warms up geolocation
      navigator.geolocation.getCurrentPosition(function () {
        // empty
      },
        function () {
          // empty
        }, {}
      );
      //The working next statement.
      navigator.geolocation.getCurrentPosition(function (position) {
        //Your code here
        userLocation.current = { lat: position.coords.latitude, lng: position.coords.longitude };
        doCenterMap && setMapCenterCoord(userLocation.current, true);
      }, function (e) {
        //Your error handling here
        doErrorAlert &&
          alert('Unable to retrieve location from device.  Please ensure location services are enabled to position map to current location.');
        backupCoord !== undefined && setMapCenterCoord(backupCoord);
      }, {
        enableHighAccuracy: true
      });
    }
  }, [setMapCenterCoord]);

  const centerMapToLocation = useCallback(function () {
    userLocation.current === null
      ? setToUserLocation(true)
      : setMapCenterCoord(userLocation.current, true)
      ;
  }, [setMapCenterCoord, setToUserLocation]);

  const setMapToGoogleGeocode = useCallback(function (value: google.maps.GeocoderResult[]) {
    if (value.length > 0) {
      setMapCenterCoord({ lat: value[0].geometry.location.lat(), lng: value[0].geometry.location.lng() }, true);
    }
  }, [setMapCenterCoord]);

  const setMapCenterToPlaceId = useCallback(function (placeId: string) {
    geocodeByPlaceId(placeId)
      .then(results => setMapToGoogleGeocode(results))
      .catch(error => alert('Location can not be found.'));
  }, [setMapToGoogleGeocode]);

  const setLocationAutocompleteValueWrapper = useCallback(function (location: google.maps.places.AutocompletePrediction) {
    if (location !== null && location.place_id !== undefined) {
      setLocationAutocompleteValue(location);
      setMapCenterToPlaceId(location.place_id);
    }
  }, [setMapCenterToPlaceId]);

  const centerMapToPlace = useCallback(function () {
    if (locationAutocompleteValue !== null && locationAutocompleteValue.place_id !== undefined && locationAutocompleteValue.place_id !== null) {
      setMapCenterToPlaceId(locationAutocompleteValue.place_id);
    }
  }, [locationAutocompleteValue, setMapCenterToPlaceId]);

  const listingSearch = <ListingSearch
    searchType={searchType}
    isHeaderDisabled={isHeaderDisabled}
    locationAutocompleteValue={locationAutocompleteValue}
    mapCenterCoord={mapCenterCoord.current}
    mapFitBounds={mapFitBounds.current}
    isMapCenterInitialized={isMapCenterInitialized}
    savedListings={savedListings}
    forcedSearchParams={forcedSearchParams}
    isSavedListing={isSavedListing}
    addSavedListing={addSavedListing}
    removeSavedListing={removeSavedListing}
    setMapCenterCoord={setMapCenterCoord}
    centerMapToLocation={centerMapToLocation}
    setMapCenterToPlaceId={setMapCenterToPlaceId}
    setLocationAutocompleteValue={setLocationAutocompleteValueWrapper}
    centerMapToPlace={centerMapToPlace}
    setPrevSearchUrl={setPrevSearchUrl}
  />

  const initializeMap = useMemo(
    () => isMapCenterInitialized ? <div></div> :
      <InitializeMapCenter isMapCenterInitialized={isMapCenterInitialized}
        setToUserLocation={setToUserLocation}
        setMapCenterCoord={setMapCenterCoord}
        setMapFitBounds={setMapFitBounds}
      />,
    [isMapCenterInitialized, setMapCenterCoord, setMapFitBounds, setToUserLocation]
  );

  const defaultElement = <div />;

  useEffect(() => {
    ENV === 'dev'
      ? setBranding(exampleBranding)
      : fetch('/wp-json/realweb-listings-addons_api/v1/settings')
        .then(response => response.json())
        .then(data => setBranding(data));
  }, []);

  useEffect(() => {
    const storedObject = localStorage.getItem('savedListings');
    if (storedObject) {
      setSavedListings(JSON.parse(storedObject));
    }
  }, []);

  // Save object to localStorage when it changes
  useEffect(() => {
    if (savedListings) {
      localStorage.setItem('savedListings', JSON.stringify(savedListings));
    }
  }, [savedListings]);

  useEffect(() => {
    branding.search_map_height_offset !== undefined &&
      document.documentElement.style.setProperty('--search-map-height-offset', branding.search_map_height_offset);
  }, [branding]);

  if (loadError) {
    return <div className="map-search">Unable to load Google Map.</div>
  }

  if (!isLoaded) {
    return <div />
  }

  return (
    <HelmetProvider>
      <div className="RealWeb_ListingsAddons">
        <BrowserRouter>
          <Routes>
            <Route path="*" element={listingSearch} />
          </Routes>
          <Routes>
            <Route path="/search/*" element={initializeMap} />
            <Route path="/details/:slug"
              element={<ListingDetailsIndex
                closeUrl={prevSearchUrl}
                savedListings={savedListings}
                branding={branding}
                setMapCenterCoord={setMapCenterCoord}
                addSavedListing={addSavedListing}
                removeSavedListing={removeSavedListing}
                isSavedListing={isSavedListing}
              />} />
            <Route path="*" element={defaultElement} />
          </Routes>
        </BrowserRouter>
      </div>
    </HelmetProvider>
  );
}

export default App;
